import { css } from '@emotion/react';
import PropTypes from "prop-types";
import React from 'react';
import { EuiCodeBlock, EuiText, EuiSplitPanel, EuiSpacer, EuiTitle, useEuiTheme, EuiBadge, logicalCSS, useIsWithinBreakpoints, useEuiPaddingCSS } from '../../../../../src';
export var ThemeExample = ({
  color,
  type = 'token',
  title,
  description,
  example,
  examplePanel,
  snippet,
  snippetLanguage = 'jsx',
  props
}) => {
  const {
    euiTheme
  } = useEuiTheme();
  const isLargeBreakpoint = useIsWithinBreakpoints(['m', 'l', 'xl']);
  const finalSnippet = snippetLanguage === 'emotion' ? `css\`
  ${snippet}
\`` : snippet;
  return <>
      <EuiSplitPanel.Outer color={isLargeBreakpoint ? color || 'transparent' : 'subdued'} direction="row" css={/*#__PURE__*/css("gap:", euiTheme.size.xl, ";", useEuiPaddingCSS(isLargeBreakpoint ? 'vertical' : undefined).xl, ";:not(:first-child){margin-block-start:", euiTheme.size.xl, ";};label:ThemeExample;")}>
        <EuiSplitPanel.Inner paddingSize="none" style={{
        flexShrink: 0
      }}>
          {title && <>
              <EuiTitle size="xxs">
                <h3>
                  {title}{' '}
                  {type && <EuiBadge css={/*#__PURE__*/css(logicalCSS('margin-left', euiTheme.size.xs), ";;label:ThemeExample;")} color={type.includes(' ') ? 'accent' : 'hollow'}>
                      {type}
                    </EuiBadge>}
                </h3>
              </EuiTitle>

              <EuiSpacer />
            </>}

          <EuiText size="s" grow={false}>
            {description}
          </EuiText>
          {props && <>
              <EuiSpacer />
              <EuiCodeBlock transparentBackground paddingSize="none" language="ts">
                {props}
              </EuiCodeBlock>
            </>}
        </EuiSplitPanel.Inner>

        {(example || snippet) && <EuiSplitPanel.Inner paddingSize="none" style={{
        overflow: 'hidden'
      }}>
            <EuiSplitPanel.Outer direction="column" hasBorder={true} hasShadow={false}>
              {example && <EuiSplitPanel.Inner {...examplePanel}>
                  {example}
                </EuiSplitPanel.Inner>}
              <EuiSplitPanel.Inner paddingSize="none" color="subdued">
                {finalSnippet && <EuiCodeBlock whiteSpace="pre" isCopyable={true} paddingSize="m" transparentBackground={true} language={snippetLanguage === 'emotion' ? 'jsx' : snippetLanguage}>
                    {finalSnippet}
                  </EuiCodeBlock>}
              </EuiSplitPanel.Inner>
            </EuiSplitPanel.Outer>
          </EuiSplitPanel.Inner>}
      </EuiSplitPanel.Outer>
    </>;
};
ThemeExample.propTypes = {
  color: PropTypes.any,
  title: PropTypes.node,
  description: PropTypes.node,
  type: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf([null])]),
  property: PropTypes.string,
  example: PropTypes.node,
  examplePanel: PropTypes.shape({
    /**
       * Padding for all four sides
       */
    paddingSize: PropTypes.any,

    /**
       * When true the panel will grow in height to match `EuiFlexItem`
       */
    grow: PropTypes.bool,
    panelRef: PropTypes.any,

    /**
       * Background color of the panel;
       * Usually a lightened form of the brand colors
       */
    color: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }),
  snippet: PropTypes.node,
  snippetLanguage: PropTypes.any,
  props: PropTypes.node,
  provider: PropTypes.shape({
    property: PropTypes.string,
    type: PropTypes.string
  })
};

try {
  ThemeExample.__docgenInfo = {
    tags: {},
    filePath: '/app/src-docs/src/views/theme/_components/_theme_example.tsx',
    description: '',
    displayName: 'ThemeExample',
    methods: [],
    props: {
      color: {
        defaultValue: null,
        description: '',
        name: 'color',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"subdued" | "success" | "accent" | "danger" | "warning" | "transparent" | "primary" | "plain"',
          value: [{
            value: '"subdued"'
          }, {
            value: '"success"'
          }, {
            value: '"accent"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"transparent"'
          }, {
            value: '"primary"'
          }, {
            value: '"plain"'
          }]
        }
      },
      title: {
        defaultValue: null,
        description: '',
        name: 'title',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      description: {
        defaultValue: null,
        description: '',
        name: 'description',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      type: {
        defaultValue: {
          value: 'token'
        },
        description: '',
        name: 'type',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      property: {
        defaultValue: null,
        description: '',
        name: 'property',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      example: {
        defaultValue: null,
        description: '',
        name: 'example',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      examplePanel: {
        defaultValue: null,
        description: '',
        name: 'examplePanel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLDivElement> & Omit<_EuiPanelProps, "hasBorder" | "hasShadow" | "borderRadius">'
        }
      },
      snippet: {
        defaultValue: null,
        description: '',
        name: 'snippet',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      snippetLanguage: {
        defaultValue: {
          value: 'jsx'
        },
        description: '',
        name: 'snippetLanguage',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      props: {
        defaultValue: null,
        description: '',
        name: 'props',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      provider: {
        defaultValue: null,
        description: '',
        name: 'provider',
        parent: undefined,
        declarations: [{
          fileName: 'app/src-docs/src/views/theme/_components/_theme_example.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '{ property?: string; type?: string; }'
        }
      }
    },
    extendedInterfaces: []
  };
} catch (e) {}